import { IKnob } from '../IComponents.js';

/**
 * SkeuoKnob - Realistic 3D knob using CSS
 */
export class SkeuoKnob extends IKnob {
    constructor(container, options) {
        super(container, options);
        this.value = options.value ?? options.min ?? 0;
        this.isDragging = false;
        this.startY = 0;
        this.startValue = 0;
        this._create();
    }

    _create() {
        const { min = 0, max = 100, value, label } = this.options;

        this.element = document.createElement('div');
        this.element.className = 'skeuo-knob-group';

        // Knob container
        this.knobOuter = document.createElement('div');
        this.knobOuter.className = 'skeuo-knob-outer';

        this.knobInner = document.createElement('div');
        this.knobInner.className = 'skeuo-knob-inner';

        // Indicator line
        this.indicator = document.createElement('div');
        this.indicator.className = 'skeuo-knob-indicator';
        this.knobInner.appendChild(this.indicator);

        this.knobOuter.appendChild(this.knobInner);
        this.element.appendChild(this.knobOuter);

        // Label
        if (label) {
            this.labelEl = document.createElement('span');
            this.labelEl.className = 'skeuo-knob-label';
            this.labelEl.textContent = label;
            this.element.appendChild(this.labelEl);
        }

        // Value display
        this.valueEl = document.createElement('span');
        this.valueEl.className = 'skeuo-knob-value';
        this.element.appendChild(this.valueEl);

        // Set initial rotation
        this._updateRotation();
        this._updateValueDisplay();

        // Mouse/touch interaction
        this._setupInteraction();

        this.container.appendChild(this.element);
    }

    _setupInteraction() {
        const onStart = (e) => {
            e.preventDefault();
            this.isDragging = true;
            this.startY = e.clientY || e.touches?.[0]?.clientY || 0;
            this.startValue = this.value;
            this.knobOuter.classList.add('active');
            document.addEventListener('mousemove', onMove);
            document.addEventListener('mouseup', onEnd);
            document.addEventListener('touchmove', onMove);
            document.addEventListener('touchend', onEnd);
        };

        const onMove = (e) => {
            if (!this.isDragging) return;
            const clientY = e.clientY || e.touches?.[0]?.clientY || 0;
            const deltaY = this.startY - clientY;
            const { min = 0, max = 100 } = this.options;
            const range = max - min;
            const sensitivity = range / 150; // 150px = full range

            let newValue = this.startValue + (deltaY * sensitivity);
            newValue = Math.max(min, Math.min(max, newValue));

            if (newValue !== this.value) {
                this.value = newValue;
                this._updateRotation();
                this._updateValueDisplay();
                this._emitChange(this.value);
            }
        };

        const onEnd = () => {
            this.isDragging = false;
            this.knobOuter.classList.remove('active');
            document.removeEventListener('mousemove', onMove);
            document.removeEventListener('mouseup', onEnd);
            document.removeEventListener('touchmove', onMove);
            document.removeEventListener('touchend', onEnd);
        };

        this.knobOuter.addEventListener('mousedown', onStart);
        this.knobOuter.addEventListener('touchstart', onStart);

        // Mouse wheel support
        this.knobOuter.addEventListener('wheel', (e) => {
            e.preventDefault();
            const { min = 0, max = 100, step = 1 } = this.options;
            const delta = e.deltaY > 0 ? -step : step;
            const range = max - min;
            const increment = step || range / 100;

            let newValue = this.value + (delta > 0 ? increment : -increment) * 5;
            newValue = Math.max(min, Math.min(max, newValue));

            if (newValue !== this.value) {
                this.value = newValue;
                this._updateRotation();
                this._updateValueDisplay();
                this._emitChange(this.value);
            }
        });
    }

    _updateRotation() {
        const { min = 0, max = 100 } = this.options;
        const percent = (this.value - min) / (max - min);
        const angle = -135 + (percent * 270); // -135° to +135°
        this.knobInner.style.transform = `rotate(${angle}deg)`;
    }

    _updateValueDisplay() {
        const { unit = '', decimals } = this.options;
        let displayValue = this.value;

        if (typeof decimals === 'number') {
            displayValue = this.value.toFixed(decimals);
        } else if (unit === '%') {
            displayValue = Math.round(this.value * 100);
        } else if (unit === 'Hz' && this.value >= 1000) {
            displayValue = (this.value / 1000).toFixed(1) + 'k';
        } else {
            displayValue = Math.round(this.value);
        }

        this.valueEl.textContent = displayValue + (unit && unit !== '%' ? ' ' + unit : unit);
    }

    getValue() {
        return this.value;
    }

    setValue(value) {
        this.value = value;
        this._updateRotation();
        this._updateValueDisplay();
    }
}
